import type { Ref } from 'vue';

import { createInjectionContext } from '~kui/utils/createInjectionContext';

import type { CoachActivityInfoType } from '~/graphql/generated/graphql';
import type { CoachActivityType, CollectionCompletionContext } from '~/types/unified';

export type CloseHandler = () => boolean;

export interface WriteActivityInfoEventParams {
  activityInfoType: CoachActivityInfoType;
  data: Record<string, unknown>;
}

export interface ActivityOrchestratorContext {
  navigateToActivity: (activity: CoachActivityType, activityArgs: Record<string, unknown>) => void;
  /**
   * Signals the child flow is exiting and the parent should advance / unmount.
   *
   * Contract for child orchestrators: if the user actually completed the asset,
   * call `onAssetCompleted(assetId)` FIRST, then `completeFlow()`. They are
   * separate signals — `onAssetCompleted` does the parent-side bookkeeping
   * (collection analytics, BE `completeProgress`, completion view-model),
   * while `completeFlow` only handles coach page routing. Skipping
   * `onAssetCompleted` silently drops analytics + server-side progress for
   * that asset.
   */
  completeFlow: () => void;
  cancelFlow: () => void;
  /**
   * The shell's own way out of the open activity, which is what its close or back button does: the
   * activity is asked first (a collection with progress asks the student to confirm), and an
   * unclaimed close leaves: back to the page it was opened from, or to `fallback` (else the chat) when
   * there is nothing of ours behind it.
   */
  requestClose?: (fallback?: string) => void;
  navigateAway: (to: string) => Promise<void>;
  /**
   * Only the public-activity page implements this: a flow starting there has to create the ghost
   * user it will be written against before anything else happens. A signed-in surface leaves it
   * absent — the student already exists, and a flow starting is not news to anything else.
   */
  notifyFlowStarted?: () => void | Promise<void>;
  pushCloseHandler: (handler: CloseHandler) => void;
  popCloseHandler: () => void;
  onProgressUpdate?: (collectionId: string, completedCount: number) => void;
  /**
   * Child orchestrators must call this when the user successfully completes
   * the asset, BEFORE calling `completeFlow()`. See `completeFlow` for the
   * full contract.
   */
  onAssetCompleted?: (assetId: string) => void | Promise<void>;
  collectionCompletion?: Ref<CollectionCompletionContext | null>;
  /**
   * Inside a collection: whether every other asset in it is already complete, so finishing this
   * one finishes the collection. Unlike `collectionCompletion`, it doesn't wait on saving progress.
   */
  isLastRemainingAsset?: (assetId: string) => boolean;
  writeActivityInfoEvent?: (params: WriteActivityInfoEventParams) => void;
  isInsideCredenza?: boolean;
  previewMode?: boolean;
}

const { provideContext, useContext } = createInjectionContext<ActivityOrchestratorContext>('activityOrchestrator');

export const createCloseHandlerStack = () => {
  const handlers: CloseHandler[] = [];
  return {
    push: (handler: CloseHandler) => { handlers.push(handler); },
    pop: () => { handlers.pop(); },
    requestClose: () => {
      // try handlers from top to bottom, stop when one handles it (returns true)
      return [...handlers].reverse().some(handler => handler());
    },
  };
};

export const provideActivityOrchestrator = (ctx: ActivityOrchestratorContext) => provideContext(ctx);
export const useActivityOrchestrator = () => useContext();
